import { FC, useEffect, useState, useMemo } from 'react'
import { Text, Grid, GridItem } from '@chakra-ui/react'
import Empty from '../../../components/Empty'
import CustomTable from '../../../components/CustomTable'
import { ColumnDef, SortingState } from '@tanstack/react-table'
import { useNavigate } from 'react-router-dom'
import _ from 'lodash'
import { useSnapshot } from 'valtio'
import useSWR from 'swr'

import tableStore from '../../../lib/store/admin_withdraw_table'
import TableFilter, {
  IDropDown,
  ITableFilter,
} from '../../../components/TableFilter'
import { IDataResponse, serialize } from '../../../network/request'
import Gaps from '../../../components/Gaps'
import AdminWithdrawService, {
  Withdraw,
  withdrawStatusText,
} from '../../../network/services/adminWithdraw'
import toJapaneseDate from '../../../functions/toJapaneseDate'
import IndeterminateCheckbox from '../../../components/IndeterminateCheckbox'

const AdminWithdrawTable: FC<{ total: number; overridePage?: number }> = ({
  total,
  overridePage,
}) => {
  const navigate = useNavigate()
  const { state } = useSnapshot(tableStore)
  const [currentFilters, setCurrentFilters] = useState<any>(null)
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([])
  const [visible, setVisible] = useState(false)

  const setPage = (page: number) => (tableStore.state.currentPage = page)
  const setSize = (size: number) => (tableStore.state.pageSize = size)

  const [sorting, setSorting] = useState<SortingState>([])
  if (sorting.length > 0)
    tableStore.state.sort = `${sorting[0].id}:${
      sorting[0].desc ? 'desc' : 'asc'
    }`

  const { data: response } = useSWR<IDataResponse<Withdraw>>(
    serialize(AdminWithdrawService.getAll, {
      page: overridePage ?? state.currentPage,
      limit: state.pageSize,
      sort: state.sort ?? 'created_at:desc',
      refresh: state.refresh,
      ...state.filters,
    })
  )
  const rows = response ? AdminWithdrawService.toRow(response) : []
  const lastPage = response?.meta?.last_page ? response?.meta?.last_page : 1

  const columns = useMemo<ColumnDef<Withdraw, any>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <IndeterminateCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <IndeterminateCheckbox
            {...{
              checked: row.getIsSelected(),
              disabled: !row.getCanSelect(),
              indeterminate: row.getIsSomeSelected(),
              onChange: row.getToggleSelectedHandler(),
            }}
          />
        ),
      },
      {
        id: 'id',
        accessorKey: 'id',
        header: 'ID',
      },
      {
        id: 'name',
        accessorFn: (row) =>
          `${row.user?.affiliate?.last_name ?? ''} ${
            row.user?.affiliate?.first_name ?? ''
          }`,
        header: '申込者',
        enableSorting: false,
      },
      {
        id: 'email',
        accessorFn: (row) => row.user?.email ?? '',
        header: '申込者メールアドレス',
        enableSorting: false,
      },
      {
        id: 'currency',
        accessorKey: 'currency',
        header: '通貨ID',
      },
      {
        id: 'issued_at',
        accessorKey: 'created_at',
        header: '申請日',
        cell: (info) =>
          info.getValue() ? toJapaneseDate(new Date(info.getValue())) : '-',
        enableSorting: false,
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: '審査状態',
        cell: (info) => (
          <Text
            style={{
              color:
                withdrawStatusText[
                  info.getValue() as keyof typeof withdrawStatusText
                ].color,
            }}
          >
            {
              withdrawStatusText[
                info.getValue() as keyof typeof withdrawStatusText
              ].text
            }
          </Text>
        ),
        enableSorting: false,
      },
      {
        id: 'amount',
        accessorKey: 'amount',
        header: '金額',
        cell: (info) => `￥${info.getValue()}`,
      },
    ],
    []
  )

  // content={
  //   initialValue.variants.length > 0
  //     ? initialValue.variants.map((item) => item.name).join(',')
  //     : '-'
  // }

  const filterColumns: ITableFilter[] = [
    { key: 'email', name: 'メールアドレス' },
  ]

  filterColumns.forEach((item) => {
    if (
      tableStore.state?.filters &&
      Object.keys(tableStore.state.filters).includes(item.key)
    ) {
      item.default_value = tableStore.state.filters[item.key]
      item.default = true
    }
  })

  const filterDropDowns: IDropDown[] = [
    {
      default: '',
      key: 'status',
      width: 120,
      value: [
        {
          key: 'pending',
          name: '審査中',
        },
        {
          key: 'approved',
          name: '承認された',
        },
        {
          key: 'rejected',
          name: '拒否された',
        },
        {
          key: '',
          name: '全部',
        },
      ],
    },
  ]

  filterDropDowns.forEach((item) => {
    if (
      tableStore.state?.filters &&
      Object.keys(tableStore.state.filters).includes(item.key)
    ) {
      const currentValue = tableStore.state.filters[item.key]
      item['default'] = currentValue
    }
  })

  useEffect(() => {
    if (currentFilters != null) {
      tableStore.state.filters = { ...currentFilters }
      tableStore.state.currentPage = 1
    }
  }, [currentFilters])

  const handleMenuClick = (e: any) => {
    setVisible(true)
    // setModalType(e.key)
  }

  const batchDeleteRows = async () => {
    // try {
    //   for (const id of selectedRowKeys) {
    //     const { data } = await ReviewService.remove(id)
    //     if (data?.success === true) {
    //       message.success(`Delete source success`)
    //     } else {
    //       message.error(`Delete source ${id} failed`)
    //     }
    //     tableStore.state.refresh = Math.random()
    //   }
    //   setVisible(false)
    // } catch (e) {
    //   message.error({ content: e.message, className: 'message-error' })
    // }
  }

  // const ActionModal: FC = () => {
  //   return (
  //     <Modal
  //       title={`Remove ${selectedRowKeys.length} reviews`}
  //       open={visible}
  //       onOk={() => {
  //         switch(modalType){
  //           case 'delete':
  //             batchDeleteRows()
  //             break;
  //           case 'publish':

  //         }
  //       }}
  //       onCancel={() => {
  //         setVisible(false)
  //       }}
  //       okText='Delete'
  //       cancelText='Cancel'
  //     >
  //       This can't be undone
  //     </Modal>
  //   )
  // }

  return (
    <>
      {/* <ActionModal /> */}

      <Grid templateColumns='repeat(2, 1fr)' gap={2}>
        <GridItem>
          {/* {selectedRowKeys?.length > 0 ? (
            <Dropdown
              menu={{
                items: [
                  {
                    label: 'Delete',
                    onClick: handleMenuClick,
                    key: 'delete',
                  },
                ],
              }}
              trigger={['click']}
            >
              <Button type='primary'>
                More Actions <CaretDownOutlined />
              </Button>
            </Dropdown>
          ) : (
            <></>
          )} */}
        </GridItem>
        <GridItem colSpan={2}>
          <TableFilter
            dropdowns={filterDropDowns}
            columns={filterColumns}
            hasDate={false}
            setCurrentFilters={setCurrentFilters}
          />
          <Gaps level={3} />
        </GridItem>
      </Grid>

      {rows?.length > 0 ? (
        <CustomTable
          {...{
            data: rows,
            columns,
            lastPage,
            page: state.currentPage,
            setPage,
            size: state.pageSize,
            selectedRowKeys,
            setSize,
            sorting,
            setSorting,
            onClickRow: (record) => {
              if (selectedRowKeys?.length > 0) {
                if (selectedRowKeys.includes(record.id)) {
                  const filterKeys = _.filter(
                    [...selectedRowKeys],
                    function (o) {
                      return o !== record.id
                    }
                  )
                  setSelectedRowKeys([...filterKeys])
                } else {
                  setSelectedRowKeys([...selectedRowKeys, record.id])
                }
              } else {
                navigate(`${record.id}`)
              }
            }, // click row
          }}
        />
      ) : (
        <Empty description={'データが見つかりませんでした'} />
      )}
    </>
  )
}

export default AdminWithdrawTable
